/* eslint-disable no-underscore-dangle */
import { ReactNode } from 'react';
import Swal from 'sweetalert2';

import { store } from 'redux/store';
import { translate } from 'hooks/useTranslate';

import { User } from 'types/User';

import eventTypes from 'constants/eventTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { deleteSocietyById } from 'modules/society/redux/societyActions';

import trackEvent from '../../../utils/trackEvent';
import { getSocietyDeletionPreview } from '../api/getSocietyDeletionPreview';

type SocietyEntityConfig = {
  key: string;
  labelKey: string;
  icon: string;
};

type DeletionPreview = {
  partners: number;
  shares: number;
  beneficiaries: number;
  investors: number;
  operations: number;
  drafts: number;
  stockPlans: number;
  boards: number;
  holdingClasses: number;
  tenderOffers: number;
  tenderOfferShares: number;
  tradingOffers: number;
  tradingOfferOperations: number;
  communications: number;
  employees: number;
  employeesPlans: number;
  councils: number;
  committees: number;
  committeeMembers: number;
  sharesTitles: number;
  sharesOwnershipCertificates: number;
  historicShares: number;
  envelopes: number;
  folders: number;
  observations: number;
  notes: number;
  realties: number;
  partnerCategories: number;
  partnerRepresentants: number;
  customMovements: number;
  notifications: number;
  documents: {
    private: number;
    shared: number;
  };
  [key: string]: number | { private: number; shared: number };
};

type DeleteSocietyParams = {
  societyName: string;
  user: User;
  mixpanel: unknown;
};

const ENTITY_CONFIG: SocietyEntityConfig[] = [
  { key: 'partners', labelKey: 'Partners', icon: 'ni-users' },
  { key: 'shares', labelKey: 'Shares', icon: 'ni-pie' },
  { key: 'beneficiaries', labelKey: 'Beneficiaries', icon: 'ni-user-check' },
  { key: 'investors', labelKey: 'Investors', icon: 'ni-trend-up' },
  { key: 'operations', labelKey: 'Operations', icon: 'ni-tranx' },
  { key: 'drafts', labelKey: 'Drafts', icon: 'ni-file-doc' },
  { key: 'stockPlans', labelKey: 'StockPlans', icon: 'ni-gift' },
  { key: 'boards', labelKey: 'Boards', icon: 'ni-briefcase' },
  { key: 'holdingClasses', labelKey: 'HoldingClasses', icon: 'ni-layers' },
  { key: 'tenderOffers', labelKey: 'TenderOffers', icon: 'ni-tag' },
  { key: 'tenderOfferShares', labelKey: 'TenderOfferShares', icon: 'ni-files' },
  { key: 'tradingOffers', labelKey: 'TradingOffers', icon: 'ni-bar-chart' },
  {
    key: 'tradingOfferOperations',
    labelKey: 'TradingOfferOperations',
    icon: 'ni-swap',
  },
  { key: 'communications', labelKey: 'Communications', icon: 'ni-mail' },
  { key: 'employees', labelKey: 'Employees', icon: 'ni-user' },
  { key: 'employeesPlans', labelKey: 'EmployeesPlans', icon: 'ni-clipboard' },
  { key: 'councils', labelKey: 'Councils', icon: 'ni-award' },
  { key: 'committees', labelKey: 'Committees', icon: 'ni-shield' },
  {
    key: 'committeeMembers',
    labelKey: 'CommitteeMembers',
    icon: 'ni-users-fill',
  },
  { key: 'sharesTitles', labelKey: 'SharesTitles', icon: 'ni-file-img' },
  {
    key: 'sharesOwnershipCertificates',
    labelKey: 'SharesOwnershipCertificates',
    icon: 'ni-file-check',
  },
  { key: 'historicShares', labelKey: 'HistoricShares', icon: 'ni-clock' },
  { key: 'envelopes', labelKey: 'Envelopes', icon: 'ni-send' },
  { key: 'folders', labelKey: 'Folders', icon: 'ni-folder' },
  { key: 'observations', labelKey: 'Observations', icon: 'ni-eye' },
  { key: 'notes', labelKey: 'Notes', icon: 'ni-note-add' },
  { key: 'realties', labelKey: 'Realties', icon: 'ni-home' },
  {
    key: 'partnerCategories',
    labelKey: 'PartnerCategories',
    icon: 'ni-tag-alt',
  },
  {
    key: 'partnerRepresentants',
    labelKey: 'PartnerRepresentants',
    icon: 'ni-user-circle',
  },
  { key: 'customMovements', labelKey: 'CustomMovements', icon: 'ni-shuffle' },
  { key: 'notifications', labelKey: 'Notifications', icon: 'ni-bell' },
  {
    key: 'documents.private',
    labelKey: 'PrivateDocuments',
    icon: 'ni-file-text',
  },
];

const getEntityCount = (preview: DeletionPreview, key: string): number => {
  if (key === 'documents.private') return preview.documents?.private ?? 0;

  const value = preview[key];
  return typeof value === 'number' ? value : 0;
};

const buildPreviewHtml = (
  preview: DeletionPreview,
  societyName: string
): string => {
  const renderRow = ({ key, labelKey, icon }: SocietyEntityConfig): string => {
    const count = getEntityCount(preview, key);
    const hasItems = count > 0;
    const badgeBg = hasItems ? '#e14954' : '#8094ae';
    const rowOpacity = hasItems ? '1' : '0.55';

    return `
      <div style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 5px 8px;
        border-radius: 6px;
        opacity: ${rowOpacity};
        background: ${hasItems ? 'rgba(225,73,84,0.04)' : 'transparent'};
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <em class="icon ni ${icon}" style="font-size: 15px; color: ${
      hasItems ? '#526484' : '#8094ae'
    };"></em>
          <span style="font-size: 13px; color: #526484; white-space: nowrap;">${translate(
            labelKey
          )}</span>
        </div>
        <span style="
          background: ${badgeBg};
          color: #fff;
          border-radius: 20px;
          padding: 1px 9px;
          font-size: 12px;
          font-weight: 700;
          min-width: 28px;
          text-align: center;
        ">${count}</span>
      </div>`;
  };

  const mid = Math.ceil(ENTITY_CONFIG.length / 2);
  const col1 = ENTITY_CONFIG.slice(0, mid);
  const col2 = ENTITY_CONFIG.slice(mid);

  const sharedCount = preview.documents?.shared ?? 0;
  const sharedBadge = `
    <span style="
      background: #f4bd0e;
      color: #fff;
      border-radius: 20px;
      padding: 1px 9px;
      font-size: 12px;
      font-weight: 700;
      min-width: 28px;
      text-align: center;
    ">${sharedCount}</span>`;

  return `
    <div style="text-align: left; padding-bottom: 20px;">
      <p style="font-size: 13px; color: #526484; margin-bottom: 4px;">
        ${translate(
          'Society'
        )}: <strong style="color: #364a63;">${societyName}</strong>
      </p>
      <p style="font-size: 12px; color: #8094ae; margin-bottom: 12px;">
        ${translate(
          'DeletePreviewSubtitleStart'
        )} <span style="color:#e14954; font-weight:600;">${translate(
    'DeletePreviewHighlightWord'
  )}</span>.
      </p>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2px 16px; margin-bottom: 14px;">
        <div>${col1.map(renderRow).join('')}</div>
        <div>${col2.map(renderRow).join('')}</div>
      </div>

      <div style="
        border: 1.5px solid #f4bd0e;
        border-radius: 8px;
        padding: 10px 12px;
        background: #fffbee;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <em class="icon ni ni-share" style="font-size: 15px; color: #f4bd0e;"></em>
          <div>
            <span style="font-size: 13px; color: #526484; font-weight: 600;">${translate(
              'SharedDocuments'
            )}</span>
            <p style="font-size: 11px; color: #8094ae; margin: 0;">
              ${translate('SharedDocumentsWarning')}
            </p>
          </div>
        </div>
        ${sharedBadge}
      </div>
    </div>`;
};

export const deleteSociety = async (
  params: DeleteSocietyParams,
  societyId: string
): Promise<void> => {
  const { societyName, user, mixpanel } = params;

  store.dispatch(setModal((<CustomLoading />) as ReactNode));

  let preview: DeletionPreview | null = null;
  try {
    preview = (await getSocietyDeletionPreview(societyId)) as DeletionPreview;
  } catch {
    // Si falla el preview no bloqueamos el flujo de eliminación
  } finally {
    store.dispatch(setModal(null));
  }

  const html = preview
    ? buildPreviewHtml(preview, societyName)
    : `<h5 class="text-primary">${societyName}</h5><br /><div class="caption-text">${translate(
        'ThisProcedureCantUndo'
      )}</div>`;

  const CONFIRM_WORD = translate('ConfirmDeleteWord');

  Swal.fire({
    icon: 'warning',
    title: `<h4 class="nk-modal-title">${translate(
      'AreYouSureAboutDelSociety'
    )}</h4>`,
    html,
    input: 'text',
    inputPlaceholder: translate('TypeToConfirm', {
      confirmationText: CONFIRM_WORD,
    }),
    inputAttributes: { autocomplete: 'off' },
    confirmButtonText: translate('OkDelete'),
    confirmButtonColor: '#e14954',
    allowOutsideClick: false,
    showCancelButton: true,
    cancelButtonText: translate('Cancel'),
    width: preview ? 680 : undefined,
    customClass: {
      htmlContainer: 'swal-delete-preview',
      input: 'swal-delete-confirm-input',
    },
    didOpen: () => {
      const confirmBtn = Swal.getConfirmButton();
      const input = Swal.getInput();
      if (!confirmBtn || !input) return;

      confirmBtn.disabled = true;
      input.addEventListener('input', () => {
        confirmBtn.disabled = input.value !== CONFIRM_WORD;
      });
    },
    preConfirm: (value: string) => {
      if (value !== CONFIRM_WORD) {
        Swal.showValidationMessage(
          translate('MustTypeExactly', { word: CONFIRM_WORD })
        );
        return false;
      }
      return true;
    },
  }).then(async (result) => {
    if (result.isConfirmed) {
      store.dispatch(setModal((<CustomLoading />) as ReactNode));

      try {
        await (store.dispatch as any)(deleteSocietyById(societyId));
      } finally {
        store.dispatch(setModal(null));
      }

      trackEvent(mixpanel, eventTypes.DELETE_SOCIETY, {
        userId: user?._id,
        userName: user?.name,
        userEmail: user?.email,
        societyId,
        societyName,
        operation: eventTypes.DELETE_SOCIETY,
      });
    }
  });
};
